import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { celebrate } from "@/lib/celebrate";
import { Button } from "@/components/ui/button";
import { QUIZ } from "@/constants/testIds";
import { CheckCircle2, XCircle, Trophy, ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";

/** Return a human-readable label for a quiz option, supporting both
 *  legacy string options and the new `{text, image}` shape. */
function _optionLabel(opt, t) {
  if (opt == null) return "";
  if (typeof opt === "string") return opt;
  return opt.text || (opt.image ? t("quizUi.imageAnswer") : "");
}

export default function Quiz() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/quizzes/${id}`)
      .then((r) => setQuiz(r.data))
      .catch((e) => setError(formatApiError(e.response?.data?.detail, t) || e.message));
  }, [id]);

  const submit = async () => {
    if (!quiz) return;
    const missing = quiz.questions.findIndex((q) => answers[q.id] === undefined);
    if (missing !== -1) {
      toast.error(t("quizUi.pleaseAnswerQuestion", { number: missing + 1 }));
      return;
    }
    setSubmitting(true);
    try {
      const answerArr = quiz.questions.map((q) => answers[q.id]);
      const { data } = await api.post(`/quizzes/${id}/attempt`, { answers: answerArr });
      setResult(data);
      if (data.passed) {
        // Confetti celebration on chapter pass
        setTimeout(() => celebrate(), 200);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, t) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const retake = () => {
    setAnswers({});
    setResult(null);
  };

  if (error) {
    // Sequence-lock errors ("Complete every lesson", "Pass Chapter X's
    // quiz first") mean the user just clicked the quiz too early — they
    // need to go BACK to their lesson, not to Pricing. Membership/tier
    // errors are the only case where /pricing is the right destination.
    const errStr = typeof error === "string" ? error : String(error);
    const isSequenceGate = /complete every lesson|pass chapter|complete the previous/i.test(errStr);
    return (
      <div>
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-6">
          {errStr}
        </div>
        {isSequenceGate ? (
          <Button
            onClick={() => navigate(-1)}
            data-testid="quiz-back-to-lesson"
            className="mt-4 bg-blue-800 text-white hover:bg-blue-900"
          >
            {t("quizUi.backToLesson")}
          </Button>
        ) : (
          <Button
            onClick={() => navigate("/pricing")}
            data-testid="quiz-view-memberships"
            className="mt-4 bg-blue-800 text-white hover:bg-blue-900"
          >
            {t("quizUi.viewMemberships")}
          </Button>
        )}
      </div>
    );
  }

  if (!quiz) return <div className="text-slate-500">{t("quizUi.loadingQuiz")}</div>;

  // Results screen
  if (result) {
    const pct = result.score;
    return (
      <div data-testid={QUIZ.root} className="max-w-2xl mx-auto space-y-8">
        <div className="text-center">
          <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-6 ${
            result.passed ? "bg-blue-100 text-blue-800" : "bg-amber-100 text-amber-700"
          }`}>
            <Trophy size={30} />
          </div>
          <div className="text-xs uppercase tracking-[0.28em] text-blue-800 font-semibold mb-3">
            {result.passed ? t("quizUi.chapterUnlocked") : t("quizUi.almostThere")}
          </div>
          <h1 className="font-display font-black text-5xl text-slate-900 tracking-tighter">
            <span data-testid={QUIZ.resultScore}>{pct}%</span>
          </h1>
          <p className="mt-3 text-slate-600">
            {t("quizUi.correctSummary", { correct: result.correct_count, total: result.total_questions, passing: result.passing_score })}
          </p>
        </div>

        <div className="space-y-4">
          {result.results.map((r, i) => (
            <div
              key={r.id}
              className={`p-5 rounded-xl border ${
                r.is_correct
                  ? "border-blue-200 bg-blue-50/40"
                  : "border-amber-200 bg-amber-50/40"
              }`}
            >
              <div className="flex items-start gap-3">
                {r.is_correct ? (
                  <CheckCircle2 className="text-blue-700 shrink-0 mt-0.5" size={18} />
                ) : (
                  <XCircle className="text-amber-600 shrink-0 mt-0.5" size={18} />
                )}
                <div className="flex-1">
                  <div className="font-semibold text-slate-900">
                    {i + 1}. {r.prompt}
                  </div>
                  <div className="mt-2 text-sm">
                    <div className="text-slate-700">
                      {t("quizUi.yourAnswer")}{" "}
                      <span className="font-medium">
                        {_optionLabel(quiz.questions[i].options[r.selected_index], t)}
                      </span>
                    </div>
                    {!r.is_correct && (
                      <div className="text-slate-700 mt-1">
                        {t("quizUi.correct")}{" "}
                        <span className="font-medium text-blue-800">
                          {_optionLabel(quiz.questions[i].options[r.correct_index], t)}
                        </span>
                      </div>
                    )}
                    {r.explanation && (
                      <div className="mt-2 text-slate-600 italic text-sm">
                        {r.explanation}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-3 justify-center pt-4">
          <Button
            data-testid={QUIZ.retakeButton}
            onClick={retake}
            variant="outline"
            className="border-slate-300 h-11 px-6"
          >
            {t("quizUi.retakeQuiz")}
          </Button>
          <Button
            data-testid={QUIZ.backButton}
            onClick={() => navigate(`/courses/${quiz.course_id}`)}
            className="bg-blue-800 hover:bg-blue-700 text-white h-11 px-6"
          >
            {t("quizUi.backToCourse")}
          </Button>
        </div>
      </div>
    );
  }

  // Quiz-taking screen
  return (
    <div data-testid={QUIZ.root} className="max-w-2xl mx-auto space-y-8">
      <button
        onClick={() => navigate(`/courses/${quiz.course_id}`)}
        className="text-sm text-slate-500 hover:text-blue-800 inline-flex items-center gap-1"
      >
        <ArrowLeft size={14} /> {t("quizUi.backToCourse")}
      </button>
      <div>
        <div className="text-xs uppercase tracking-[0.28em] text-amber-600 font-semibold mb-3">
          {t("quizUi.chapterQuiz", { chapter: quiz.chapter_number })}
        </div>
        <h1 className="font-display font-bold text-3xl text-slate-900 tracking-tight">
          {quiz.title}
        </h1>
        <p className="mt-2 text-slate-600 text-sm">
          {t("quizUi.questionsPassInfo", { count: quiz.questions.length, passing: quiz.passing_score })}
        </p>
      </div>

      <div className="space-y-6">
        {quiz.questions.map((q, i) => {
          const hasImageOptions = (q.options || []).some((o) => o && typeof o === "object" && o.image);
          return (
            <div
              key={q.id}
              data-testid={QUIZ.question}
              className="bg-white border border-slate-200 rounded-2xl p-6"
            >
              <div className="font-display font-semibold text-slate-900 text-lg">
                {i + 1}. {q.prompt}
              </div>
              {q.prompt_image && (
                <div className="mt-4">
                  <img
                    src={q.prompt_image}
                    alt=""
                    className="max-h-72 w-auto rounded-lg border border-slate-200 object-contain bg-slate-50"
                  />
                </div>
              )}
              <div className={
                hasImageOptions
                  ? "mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3"
                  : "mt-4 space-y-2"
              }>
                {q.options.map((rawOpt, oi) => {
                  // Support both legacy string options and new {text, image} shape
                  const opt = typeof rawOpt === "string"
                    ? { text: rawOpt, image: null }
                    : (rawOpt || { text: "", image: null });
                  const selected = answers[q.id] === oi;
                  return (
                    <label
                      key={`${q.id}-opt-${oi}`}
                      data-testid={`${QUIZ.option}-${i}-${oi}`}
                      className={`${
                        hasImageOptions
                          ? "flex flex-col items-center gap-3 p-4"
                          : "flex items-start gap-3 p-3"
                      } rounded-lg border cursor-pointer transition-colors ${
                        selected
                          ? "border-blue-800 bg-blue-50/60 ring-2 ring-blue-200"
                          : "border-slate-200 hover:border-slate-400 hover:bg-slate-50"
                      }`}
                    >
                      {!hasImageOptions && (
                        <input
                          type="radio"
                          name={q.id}
                          value={oi}
                          checked={selected}
                          onChange={() => setAnswers({ ...answers, [q.id]: oi })}
                          className="mt-1 accent-blue-800"
                        />
                      )}
                      {opt.image && (
                        <img
                          src={opt.image}
                          alt=""
                          className="w-full max-h-40 object-contain rounded-md border border-slate-200 bg-slate-50"
                          onClick={() => setAnswers({ ...answers, [q.id]: oi })}
                        />
                      )}
                      {opt.text && (
                        <span className={`text-sm text-slate-800 ${hasImageOptions ? "text-center font-medium" : ""}`}>
                          {opt.text}
                        </span>
                      )}
                      {hasImageOptions && (
                        <input
                          type="radio"
                          name={q.id}
                          value={oi}
                          checked={selected}
                          onChange={() => setAnswers({ ...answers, [q.id]: oi })}
                          className="accent-blue-800"
                        />
                      )}
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex justify-end">
        <Button
          data-testid={QUIZ.submitButton}
          onClick={submit}
          disabled={submitting}
          className="bg-blue-800 hover:bg-blue-700 text-white h-11 px-8"
        >
          {submitting ? t("quizUi.submitting") : t("quizUi.submitAnswers")}
        </Button>
      </div>
    </div>
  );
}
